import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FieldErrors } from 'react-hook-form';
import type { TFunction } from 'i18next';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { marksReportService } from '@/api/domains/attendance/services/marksReport.service';
import type { MarkReport } from '@/api/domains/attendance/types/marksReport.types';
import {
  bulkAdjustMarkFormSchema,
  type BulkAdjustMarkFormValues,
} from '@/schemas/attendance/bulkAdjustMarkForm';


// Business-rule conflict code returned by the backend when every requested mark was skipped
// (e.g. all disabled/forbidden/not found) — nothing was applied nor left pending.
const CONFLICT_NONE_PROCESSED_CODE = 'CONFLICT_NO_MARKS_PROCESSED';

const MESSAGE_KEY_PREFIX = 'attendance:marksReport.adjustBulk.messages';

interface BulkAdjustTotals {
  totalApplied: number;
  totalPending: number;
  totalSkipped: number;
}

// Se arma el resumen solo con los fragmentos cuyo total es mayor a cero (mismo criterio que
// NE_BulkAdjustMarkOrchestrator.BuildSummaryMessage en el backend). Un texto fijo mostraría
// ruido como "0 enviados a validación del colaborador" en el caso más frecuente: empresas sin
// flujo de rechazo, donde totalPending siempre es 0. No se consume response.data.message
// porque el backend lo genera en español hardcodeado y rompería la i18n.
function buildSummaryMessage(
  { totalApplied, totalPending, totalSkipped }: BulkAdjustTotals,
  translate: TFunction,
): string {
  const fragments = [
    totalApplied > 0 && translate(`${MESSAGE_KEY_PREFIX}.fragmentApplied`, { count: totalApplied }),
    totalPending > 0 && translate(`${MESSAGE_KEY_PREFIX}.fragmentPending`, { count: totalPending }),
    totalSkipped > 0 && translate(`${MESSAGE_KEY_PREFIX}.fragmentSkipped`, { count: totalSkipped }),
  ].filter((fragment): fragment is string => typeof fragment === 'string');

  return `${fragments.join(', ')}.`;
}

interface UseBulkAdjustMarkFormParams {
  records: MarkReport[];
  onSubmit?: () => void;
}

export interface UseBulkAdjustMarkFormResult {
  isSubmitting: boolean;
  isReady: boolean;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
  register: ReturnType<typeof useForm<BulkAdjustMarkFormValues>>['register'];
  errors: FieldErrors<BulkAdjustMarkFormValues>;
  isSubmitted: boolean;
  newTime: string;
  newEventId: string;
  comment: string;
  handleTimeChange: (value: string) => void;
  handleTimePickerOpenChange: (isOpen: boolean) => void;
  handleEventChange: (value: string) => void;
  handleCommentChange: (value: string) => void;
}

export function useBulkAdjustMarkForm({
  records,
  onSubmit: onSubmitCallback,
}: UseBulkAdjustMarkFormParams): UseBulkAdjustMarkFormResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showWarning, showApiError, closeSidePanel } = useUI();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [newEventId, setNewEventId] = useState('');
  const [comment, setComment] = useState('');

  const {
    handleSubmit: rhfHandleSubmit,
    register,
    setError,
    setValue,
    formState,
  } = useForm<BulkAdjustMarkFormValues>({
    resolver: zodResolver(bulkAdjustMarkFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      newTime: '',
      newEventId: '',
      comment: '',
    },
  });

  // TimePicker emite un valor por defecto ('00:00:00') al montar, antes de que el usuario
  // interactúe (ver src/components/ui/inputs/TimePicker.tsx). Se ignora ese valor hasta que
  // el usuario abra el selector al menos una vez, para poder mantener "sin cambio" (null) real.
  const hasOpenedTimePickerRef = useRef(false);

  const handleTimePickerOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) hasOpenedTimePickerRef.current = true;
  }, []);

  const handleTimeChange = useCallback((value: string) => {
    if (!hasOpenedTimePickerRef.current) return;
    setNewTime(value);
    setValue('newTime', value, { shouldDirty: true });
  }, [setValue]);

  const handleEventChange = useCallback((value: string) => {
    setNewEventId(value);
    setValue('newEventId', value, { shouldDirty: true });
  }, [setValue]);

  const handleCommentChange = useCallback((value: string) => {
    setComment(value);
    setValue('comment', value, { shouldDirty: true });
  }, [setValue]);

  const isReady = useMemo(() => {
    const hasTimeOrEvent = newTime.length > 0 || newEventId.length > 0;
    const hasComment = comment.trim().length > 0;
    return hasTimeOrEvent && hasComment;
  }, [newTime, newEventId, comment]);

  const submitBulkAdjustMark = useCallback(async (data: BulkAdjustMarkFormValues) => {
    if (records.length === 0) return;
    setIsSubmitting(true);

    try {
      const response = await marksReportService.adjustMarkBulk(
        records.map((record) => record.id),
        {
          newTime: data.newTime ?? null,
          newEventId: data.newEventId ? Number(data.newEventId) : null,
          comment: data.comment,
        },
      );

      if (response.success && response.data) {
        const { totalApplied, totalPending, totalSkipped } = response.data;

        const summary = buildSummaryMessage(
          { totalApplied, totalPending, totalSkipped },
          translate,
        );

        if (totalSkipped === 0) {
          showSuccess(summary);
        } else {
          showWarning(summary);
        }

        onSubmitCallback?.();
        closeSidePanel?.();
      } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
        injectValidationErrors(response.error.validationErrors, { setError });
      } else if (response.error?.code === CONFLICT_NONE_PROCESSED_CODE) {
        showApiError(response, translate(`${MESSAGE_KEY_PREFIX}.noneProcessed`));
      } else {
        showApiError(response, translate(`${MESSAGE_KEY_PREFIX}.error`));
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    records, showSuccess, showWarning, showApiError, onSubmitCallback,
    closeSidePanel, translate, setError,
  ]);

  const onValid = useCallback(async (data: BulkAdjustMarkFormValues) => {
    await submitBulkAdjustMark(data);
  }, [submitBulkAdjustMark]);

  const onInvalid = useCallback((): void => {}, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      if (isSubmitting) return;
      const wrappedValid = (data: BulkAdjustMarkFormValues) => { void onValid(data); };
      const wrappedInvalid = () => { onInvalid(); };
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid, onInvalid, isSubmitting],
  );

  return {
    isSubmitting,
    isReady,
    handleSubmit,
    register,
    errors: formState.errors,
    isSubmitted: formState.isSubmitted,
    newTime,
    newEventId,
    comment,
    handleTimeChange,
    handleTimePickerOpenChange,
    handleEventChange,
    handleCommentChange,
  };
}

export default useBulkAdjustMarkForm;
